import classNames from 'classnames'
import { EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react'
import {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  useEffect,
  useState,
} from 'react'
import { CloseIcon } from '~/theme/icons'
import { focusRing } from '~/utils/styles'

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  hasError?: boolean
}

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

export const baseClass = classNames(
  'outline-hidden text-body-1 flex rounded-2xl px-4 py-2.5 border-1 border-grey-200 text-grey-900 bg-white w-full',
  'focus:border-blue ',
  'disabled:cursor-not-allowed disabled:border-grey-200 disabled:bg-grey-100 disabled:text-grey-700',
)

const inputClass = classNames(
  baseClass,
  'read-only:cursor-not-allowed read-only:border-grey-200 read-only:bg-grey-100 read-only:text-grey-700',
)

const iconClasses = 'text-blue'
const iconErrorClasses = 'text-red'

const shouldHideIcon = (type: string) =>
  ['date', 'time', 'datetime-local', 'number', 'hidden'].includes(type)

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      hasError,
      onChange,
      className,
      defaultValue,
      type: __type = 'text',
      ...props
    },
    ref,
  ) => {
    const [_input, setInput] = useState(defaultValue || props.value)
    const [_showPass, setShowPass] = useState(false)
    const [_inputType, setInputType] = useState(__type)
    const [showButton, setShowButton] = useState(!shouldHideIcon(__type))

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      setInput(e.target.value)
      onChange?.(e)
    }

    useEffect(() => {
      if (shouldHideIcon(__type)) {
        setShowButton(false)
      }
    }, [__type])

    const handleIconClick = () => {
      if (_inputType === 'password') {
        setShowPass(true)
        setInputType('text')
      } else if (_showPass) {
        setShowPass(false)
        setInputType('password')
      } else {
        setInput('')
        onChange?.({
          target: { value: '' },
        } as ChangeEvent<HTMLInputElement>)
      }
    }

    useEffect(() => {
      setInput(props.value)
    }, [props.value])

    return (
      <div className={'relative w-full'}>
        <input
          ref={ref}
          className={classNames(
            className,
            inputClass,
            hasError && 'text-red! border-red!',
          )}
          {...props}
          type={_inputType}
          value={_input}
          onChange={handleInputChange}
        />
        {props.readOnly && (
          <LockIcon
            aria-hidden='true'
            focusable='false'
            className={
              'bg-grey-400 absolute top-3 right-4 h-6 w-6 rounded-md p-1 text-white outline-none'
            }
          />
        )}
        {showButton && (
          <button
            type={'button'}
            onClick={handleIconClick}
            className={classNames(
              focusRing,
              'absolute top-2.5 right-2.5 h-7 w-7 outline-none',
            )}
            hidden={props.readOnly || props.disabled || !_input}
          >
            {_showPass ? (
              <EyeIcon
                className={classNames(
                  hasError ? iconErrorClasses : iconClasses,
                )}
              />
            ) : _inputType === 'password' ? (
              <EyeOffIcon
                className={classNames(
                  hasError ? iconErrorClasses : iconClasses,
                )}
              />
            ) : (
              <CloseIcon
                className={classNames(
                  hasError ? iconErrorClasses : iconClasses,
                )}
              />
            )}
          </button>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        rows={3}
        className={classNames(inputClass, 'h-auto py-1', className)}
        {...props}
      >
        {children}
      </textarea>
    )
  },
)

Textarea.displayName = 'Textarea'

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>

const selectClass = classNames(
  baseClass,
  // <select> elements don't support the `readOnly` attribute
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly#overview
  // Please note, that the element will be still interactive when setting aria-readonly="true".
  // One option is to disable the not-selected <option> elements.
  'ps-2.5 aria-readonly:cursor-not-allowed aria-readonly:border-grey-200 aria-readonly:bg-grey-100 aria-readonly:text-grey-700',
  'form-select', // please see ~/tailwinds.css for the definition of .form-select
  // Show "..." for long text
  'truncate text-ellipsis',
  // Show placeholder text in grey. This requires a <option value=''>...</option> + <select required >,
  // so that the placeholder represents an invalid selection
  'invalid:text-grey-400',
)

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <select
        ref={ref}
        className={classNames(className, selectClass)}
        {...props}
      >
        {children}
      </select>
    )
  },
)

Select.displayName = 'Select'
